/**
===================================================
Module: frontend.src.components.Sessions.tsx
Description: Sessions management page component
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Session, Project, CreateSessionRequest } from "../types";
import { apiService } from "../services/api";
import TopBar from "./TopBar";
import VideoCropper from "./VideoCropper";
import CreateSessionModal from "./CreateSessionModal";
import SessionsList from "./SessionsList";
import NotificationSystem, { Notification } from "./NotificationSystem";
import "../styles/Sessions.css";
import {
  extractVideoThumbnail,
  convertDataUrlToBlob,
} from "../utils/videoThumbnail";
import { useSessionCreationStore } from "../stores/sessionCreationStore";
import SortFilterModal, { SortOption, FilterOption } from "./SortFilterModal";

const Sessions: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false); // Add this state
  const [error, setError] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showVideoCropper, setShowVideoCropper] = useState(false);
  const [sessionDataForCrop, setSessionDataForCrop] =
    useState<CreateSessionRequest | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");

  // Add notifications state
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const { clearState, setSessionData } = useSessionCreationStore();

  // Add new state for delete functionality
  const [showMenuDropdown, setShowMenuDropdown] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState<Set<string>>(
    new Set(),
  );
  const [isDeleting, setIsDeleting] = useState(false);

  // Notification management functions
  const addNotification = (notification: Omit<Notification, "id">) => {
    const id = Date.now().toString();
    setNotifications((prev) => [...prev, { ...notification, id }]);
    return id;
  };

  const removeNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  useEffect(() => {
    if (projectId) {
      loadData();
    }
  }, [projectId]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [projectData, sessionsData] = await Promise.all([
        apiService.getProject(projectId!),
        apiService.getSessions(projectId!),
      ]);
      setProject(projectData);
      setSessions(sessionsData);
    } catch (error) {
      addNotification({
        type: "error",
        message: "ファイルのロードに失敗しました",
        duration: 8000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSession = (
    sessionData: CreateSessionRequest,
    videoFile: File,
    thumbnailBlob: Blob,
  ) => {
    setSessionData(sessionData);

    setSessionDataForCrop(sessionData);

    setShowCreateForm(false);
    setShowVideoCropper(true);
  };

  const handleVideoCrop = async (
    startTime: number,
    endTime: number,
    enableTrimming: boolean,
    duration: number,
  ) => {
    const { sessionData, videoFile, thumbnailBlob } =
      useSessionCreationStore.getState();

    if (!sessionData || !videoFile || !thumbnailBlob) return;

    // Initialize uploadNotificationId as undefined
    let uploadNotificationId: string | undefined;

    try {
      setIsUploading(true);
      setShowVideoCropper(false);

      // Add upload notification
      uploadNotificationId = addNotification({
        type: "progress",
        message: `${sessionData.name}ファイルをアップロード中。ページを更新または閉じないでください`,
        persistent: true,
      });

      const file = videoFile;

      const preprocessing = {
        enabled: enableTrimming,
        steps: enableTrimming
          ? [
              {
                type: "trim" as const,
                params: {
                  start_time: startTime,
                  end_time: endTime,
                },
              },
            ]
          : [],
      };

      const thumbnailBase64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(thumbnailBlob);
      });

      const sessionDataForUpload = {
        ...sessionData,
        video_size_bytes: file.size,
        video_duration_seconds: duration,
        thumbnail_data: thumbnailBase64,
        thumbnail_content_type: "image/jpeg",
      };

      const sessionResponse = await apiService.createSessionWithPreprocessing(
        projectId!,
        sessionDataForUpload,
        preprocessing,
      );
      await apiService.uploadVideoFile(file, sessionResponse.upload_url);
      await apiService.notifyUploadComplete(sessionResponse.id);

      // Remove upload notification and show success
      if (uploadNotificationId) {
        removeNotification(uploadNotificationId);
      }
      addNotification({
        type: "success",
        message: `${sessionData.name}ファイルのアップロードが完了しました`,
        duration: 5000,
      });

      clearState();
      setIsUploading(false);
      loadData();
    } catch (error) {
      // Remove upload notification and show error
      if (uploadNotificationId) {
        removeNotification(uploadNotificationId);
      }
      addNotification({
        type: "error",
        message: "動画のアップロードに失敗しました。時間をおいてもう一度お試しください",
        duration: 8000,
      });

      setIsUploading(false);
    }
  };

  const handleCancelVideoCropper = () => {
    setShowVideoCropper(false);
    setShowCreateForm(false);
    clearState();
  };

  const handleBackToCreateSession = () => {
    setShowVideoCropper(false);
    setShowCreateForm(true);
  };

  const handleSessionClick = (sessionId: string) => {
    // Find the session to check its status
    const session = sessions.find((s) => s.id === sessionId);

    // Only allow clicking on completed sessions
    if (session && session.status === "completed") {
      window.open(`/results/${sessionId}`, "_blank");
    }
    // If not completed, do nothing (no error, just silent)
  };

  const handleMenuClick = () => {
    setShowMenuDropdown(!showMenuDropdown);
  };

  const handleDeleteSessions = () => {
    setIsSelectionMode(true);
    setShowMenuDropdown(false);
    // Remove automatic selection - start with empty selection
    setSelectedSessions(new Set());
  };

  const handleCancelSelection = () => {
    setIsSelectionMode(false);
    setSelectedSessions(new Set());
  };

  const handleSessionSelect = (sessionId: string, isSelected: boolean) => {
    // Remove the status check - all sessions can now be deleted
    const newSelected = new Set(selectedSessions);
    if (isSelected) {
      newSelected.add(sessionId);
    } else {
      newSelected.delete(sessionId);
    }
    setSelectedSessions(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedSessions.size === 0) return;

    const confirmed = window.confirm(
      `ファイルを${selectedSessions.size}件削除してもよろしいですか? この操作は元に戻せません`,
    );

    if (!confirmed) return;

    try {
      setIsDeleting(true);
      const sessionIds = Array.from(selectedSessions);

      // Call the new bulk delete endpoint
      await apiService.deleteSessions(sessionIds);

      // Clear selection and refresh data
      setSelectedSessions(new Set());
      setIsSelectionMode(false);
      loadData();
    } catch (error) {
      addNotification({
        type: "error",
        message: "ファイルの削除に失敗しました。時間をおいてもう一度お試しください",
        duration: 8000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Add state for sort and filter
  const [showSortFilterModal, setShowSortFilterModal] = useState(false);
  const [currentSort, setCurrentSort] = useState("date-newest");
  const [currentFilters, setCurrentFilters] = useState<string[]>([]);

  // Define sort and filter options for sessions
  const sortOptions: SortOption[] = [
    { id: "name-asc", label: "名前 (昇順)", value: "name-asc" },
    { id: "name-desc", label: "名前 (降順)", value: "name-desc" },
    { id: "date-newest", label: "日付 (新→旧)", value: "date-newest" },
    { id: "date-oldest", label: "日付 (旧→新)", value: "date-oldest" },
  ];

  const filterOptions: FilterOption[] = [
    {
      id: "waiting_for_upload",
      label: "アップロード待ち",
      value: "waiting_for_upload",
      checked: false,
    },
    { id: "uploading", label: "アップロード中", value: "uploading", checked: false },
    { id: "queued", label: "処理待ち", value: "queued", checked: false },
    {
      id: "processing",
      label: "処理中",
      value: "processing",
      checked: false,
    },
    { id: "completed", label: "完了", value: "completed", checked: false },
    { id: "failed", label: "失敗", value: "failed", checked: false },
  ];

  // Add sort and filter functions
  const handleSortFilterApply = (sortBy: string, filters: string[]) => {
    setCurrentSort(sortBy);
    setCurrentFilters(filters);
  };

  const handleSortFilterReset = () => {
    setCurrentSort("date-newest");
    setCurrentFilters([]);
  };

  // Apply sorting and filtering to sessions
  const getSortedAndFilteredSessions = () => {
    let filtered = sessions.filter((session) => {
      if (currentFilters.length === 0) return true;

      return currentFilters.some((filter) => {
        return session.status === filter;
      });
    });

    // Apply sorting
    filtered.sort((a, b) => {
      switch (currentSort) {
        case "name-asc":
          return a.name.localeCompare(b.name);
        case "name-desc":
          return b.name.localeCompare(a.name);
        case "date-newest":
          return (
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        case "date-oldest":
          return (
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
        default:
          return 0;
      }
    });

    return filtered;
  };

  // Update the filtered sessions to use the new function
  const filteredSessions = getSortedAndFilteredSessions().filter((session) =>
    session.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Add new function to refresh only sessions
  const refreshSessions = async () => {
    try {
      setIsRefreshing(true);
      const sessionsData = await apiService.getSessions(projectId!);
      setSessions(sessionsData);
      addNotification({
        type: "success",
        message: "フォルダの更新が完了しました",
        duration: 3000,
      });
    } catch (error) {
      addNotification({
        type: "error",
        message: "フォルダの更新に失敗しました",
        duration: 8000,
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="sessions-container">
        <div className="sessions-content">
          <TopBar />
          <div
            style={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            ファイルの読み込み中...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sessions-container">
      <div className="sessions-content">
        <TopBar />

        <div className="project-info">
          <div className="breadcrumb">
            <span
              className="breadcrumb-link"
              onClick={() => navigate("/projects")}
            >
              フォルダ一覧
            </span>
            <span>/</span>
            <span>{project?.name}</span>
          </div>

          <div className="project-header">
            <div>
              <h1 className="project-title">{project?.name}</h1>
              <p className="project-description">
                {project?.description || "説明なし"}
              </p>
            </div>

            <div className="header-actions">
              <button
                className="refresh-btn"
                onClick={refreshSessions}
                disabled={isRefreshing || isUploading || isSelectionMode}
                title="フォルダの更新"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={isRefreshing ? "spinning" : ""}
                >
                  <path
                    d="M1 4V10H7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M23 20V14H17"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14L18.36 18.36A9 9 0 0 1 3.51 15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {isRefreshing ? "更新中..." : "更新"}
              </button>

              <button
                className="add-session-btn"
                onClick={() => setShowCreateForm(true)}
                disabled={isUploading || isSelectionMode}
              >
                <span>+</span>
                ファイルを追加
              </button>

              {isSelectionMode && selectedSessions.size > 0 && (
                <button
                  className="delete-sessions-btn"
                  onClick={handleBulkDelete}
                  disabled={isDeleting}
                >
                  {isDeleting
                    ? "削除中..."
                    : `${selectedSessions.size}件のファイルを削除`}
                </button>
              )}

              <div className="menu-container">
                <div className="menu-icon" onClick={handleMenuClick}>
                  ⋮
                </div>
                {showMenuDropdown && (
                  <div className="menu-dropdown">
                    <button
                      className="menu-item"
                      onClick={handleDeleteSessions}
                    >
                      ファイルを削除
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="main-content">
          <div className="search-section">
            <div className="search-and-filter-container">
              <div className="search-container">
                <div className="search-icon">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  className="search-input"
                  placeholder="ファイルを検索"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="clear-search-btn"
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 6L6 18M6 6L18 18"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
              </div>

              <button
                className="sort-filter-btn"
                onClick={() => setShowSortFilterModal(true)}
                title="並べ替え・フィルター"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 6H21M6 12H18M9 18H15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {(searchQuery || currentFilters.length > 0) && (
              <div className="search-results-info">
                {sessions.length}件中{filteredSessions.length}のフォルダを表示
                {currentFilters.length > 0 && (
                  <span className="filter-info">
                    • フィルター: {filterOptions.filter(option => currentFilters.includes(option.id))
                                               .map(option => option.label).join(", ")}
                  </span>
                )}
                {currentSort !== "date-newest" && (
                  <span className="sort-info">
                    • ソート:{" "}
                    {
                      sortOptions.find((opt) => opt.value === currentSort)
                        ?.label
                    }
                  </span>
                )}
              </div>
            )}
          </div>

          {isSelectionMode && (
            <div className="selection-mode-header">
              <div className="selection-info">
                <span className="selection-count">
                  ファイル選択中（{selectedSessions.size}個）
                </span>
              </div>
              <div className="selection-actions">
                <button
                  className="select-all-btn"
                  onClick={() =>
                    setSelectedSessions(
                      new Set(sessions.map((session) => session.id)),
                    )
                  }
                >
                  すべて選択
                </button>
                <button
                  className="deselect-all-btn"
                  onClick={() => setSelectedSessions(new Set())}
                >
                  すべて解除
                </button>
                <button
                  className="cancel-selection-btn"
                  onClick={handleCancelSelection}
                >
                  キャンセル
                </button>
              </div>
            </div>
          )}

          <SessionsList
            sessions={filteredSessions}
            onSessionClick={handleSessionClick}
            isLoading={isLoading}
            isSelectionMode={isSelectionMode}
            selectedSessions={selectedSessions}
            onSessionSelect={handleSessionSelect}
            onCancelSelection={handleCancelSelection}
          />
        </div>
      </div>

      <NotificationSystem
        notifications={notifications}
        onRemove={removeNotification}
      />

      <CreateSessionModal
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        onNext={handleCreateSession}
        isUploading={isUploading}
        persistState={showCreateForm && !showVideoCropper}
      />

      {showVideoCropper && (
        <VideoCropper
          onCrop={handleVideoCrop}
          onCancel={handleCancelVideoCropper}
          onBack={handleBackToCreateSession}
        />
      )}

      <SortFilterModal
        isOpen={showSortFilterModal}
        onClose={() => setShowSortFilterModal(false)}
        onApply={handleSortFilterApply}
        onReset={handleSortFilterReset}
        sortOptions={sortOptions}
        filterOptions={filterOptions}
        currentSort={currentSort}
        currentFilters={currentFilters}
        title="並べ替え・フィルター"
      />
    </div>
  );
};

export default Sessions;
